import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useTheme, useMediaQuery, Box, TextField, Button,  
    CircularProgress, Typography,  
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const FAQPage = () => {
    useAuthCheck();
    const { faqId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [faq, setFaq] = useState({});

    const initialValues = {
        title: faq.title || '',
        content: faq.content || '',
    };


    useEffect(() => {    
        const fetchFAQ = async () => {
            try {
                setLoading(true);
                if (faqId) {
                    const res = await api.get(`/faqs/${faqId}`);
                    setFaq(res.data);
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };    
        fetchFAQ();
    }, [faqId]); 

    const checkoutSchema = yup.object().shape({
        title: yup.string().required('Required'),
        content: yup.string().required('Required'),
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = faqId ? 'PUT' : 'POST';
            const url = faqId 
                ? `/faqs/update/${faqId}` 
                : '/faqs/new';

            await api({
                method,
                url,
                data: {
                    title: values.title,
                    content: values.content,
                },
            });

            toast.success(faqId 
                ? 'FAQ updated successfully' 
                : 'FAQ created successfully'
            );
            
            // onSubmitProps.resetForm();
            navigate("/faqs");
        } catch (error) {
            setError(error);
        }
    };


    return (
        <Box className="main-container-box">               
            <FlexBetween>
                <Header
                    title="FAQ"
                    subtitle={`${loading ? 'Loading...' : faqId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/faqs")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>

            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={initialValues}
                        enableReinitialize={true}
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gap={2}
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    sx={{ '& > div': { gridColumn: isDesktop ? undefined : 'span 4' } }}
                                >
                                    <TextField
                                        fullWidth
                                        required
                                        variant="filled"
                                        type="text"
                                        label="Title"
                                        name="title"
                                        value={values.title}
                                        error={!!touched.title && !!errors.title}
                                        helperText={touched.title && errors.title}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    {/* <Box
                                        height={"50vh"}                                                    
                                        sx={{ 
                                            gridColumn: 'span 12', 
                                            backgroundColor: 'green'
                                        }}
                                    >
                                        <Tiptap 
                                            content={content}
                                            onChange={setContent}
                                        />
                                    </Box> */}

                                    <TextField
                                        fullWidth
                                        required
                                        variant="filled"
                                        multiline
                                        rows={6}
                                        label="Content"
                                        name="content"
                                        value={values.content}
                                        error={!!touched.content && !!errors.content}
                                        helperText={touched.content && errors.content}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {faqId ? "Update" : "Create"}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default FAQPage;
